import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { syncUserPermissions } from '../services/permissionsSyncService';
import { fetchPermissions, setAuthState } from '../store/slices/permissionsSlice';
import { initAuthChannel, requestAuthData, broadcastAuthData, unregisterMessageHandler } from '../services/tabSyncService';

/**
 * خطاف خاص للتحقق من حالة التبويب وتهيئة الصلاحيات في التبويبات الجديدة
 * يتأكد من تهيئة التبويب الجديد من خلال استدعاء API وإعادة تحميل الصلاحيات
 */
export const useTabInitialization = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // تهيئة التبويب والتعامل مع تبادل البيانات بين التبويبات
    const initializeTab = async () => {
      // 1. شوهد تبويب جديد
      const tabId = sessionStorage.getItem('tabId');
      
      if (!tabId) {
        // تعيين معرف للتبويب الجديد
        const newTabId = Date.now().toString();
        sessionStorage.setItem('tabId', newTabId);
        // إضافة علامة لتجنب عرض شاشة تسجيل الدخول قبل محاولة المزامنة
        sessionStorage.setItem('pendingAuthSync', 'true');
        
        // 2. فحص وجود توكن في التبويب الحالي
        const token = sessionStorage.getItem('token');
        
        if (token) {
          // Loading permissions from API for new tab
          
          try {
            // 3. أولاً: استدعاء API لتحديث الصلاحيات في sessionStorage
            await syncUserPermissions();
            dispatch(fetchPermissions());
            // Permissions loaded successfully in new tab
            // إزالة علامة الانتظار
            sessionStorage.removeItem('pendingAuthSync');
          } catch (error) {
            // Failed to load permissions in new tab
            // لا نزيل علامة الانتظار لنعطي فرصة للتبويبات الأخرى
            requestAuthFromOtherTabs();
          }
        } else {
          // لا يوجد توكن، نطلب من التبويبات الأخرى
          // New tab without token: requesting data from other tabs
          requestAuthFromOtherTabs();
          
          // إضافة مهلة لانتظار الرد من التبويبات الأخرى (منع عرض شاشة تسجيل الدخول مباشرة)
          setTimeout(() => {
            // إذا لم يتم استلام بيانات المصادقة من التبويبات الأخرى خلال المهلة
            // نزيل علامة الانتظار للسماح بعرض شاشة تسجيل الدخول
            if (!sessionStorage.getItem('token')) {
              // No data received from other tabs within timeout
              sessionStorage.removeItem('pendingAuthSync');
              // إنشاء حدث لإشعار التطبيق بالتغيير
              window.dispatchEvent(new Event('authSyncCompleted'));
            }
          }, 1500); // انتظار 1.5 ثانية فقط
        }
      }
    };
    
    // تهيئة قناة المزامنة بين التبويبات
    const setupBroadcastChannel = () => {
      // معالجة الرسائل الواردة من التبويبات الأخرى
      const handleIncomingMessage = (message) => {
        if (!message) return;
        
        // Message received through broadcast channel
        
        if (message.type === 'AUTH_REQUEST') {
          // رد على طلب بيانات المصادقة بإرسال البيانات المحلية
          const token = sessionStorage.getItem('token');
          if (token) {
            const userType = sessionStorage.getItem('userType');
            const userRoleId = sessionStorage.getItem('userRoleId');
            const user = JSON.parse(sessionStorage.getItem('user') || '{}');
            
            broadcastAuthData({
              token,
              userType,
              userRoleId,
              user,
              source: 'RESPONSE_TO_REQUEST',
              timestamp: Date.now()
            });
          }
        } else if (message.type === 'AUTH_SYNC' && message.data) {
          // التحقق من وجود إشارة تسجيل الخروج
          if (message.data.action === 'LOGOUT') {
            // تنفيذ تسجيل الخروج محلياً دون إعادة توجيه
            sessionStorage.clear();
            // إعادة تحميل الصفحة بدلاً من التوجيه للصفحة الرئيسية
            window.location.reload();
            return;
          }
          
          // استلام بيانات المصادقة من تبويب آخر
          handleAuthDataFromOtherTab(message.data);
        }
      };
      
      // تهيئة قناة البث
      return initAuthChannel(handleIncomingMessage);
    };
    
    // طلب بيانات المصادقة من التبويبات الأخرى
    const requestAuthFromOtherTabs = () => {
      // Requesting authentication data from other tabs
      requestAuthData();
    };
    
    // معالجة بيانات المصادقة المستلمة من التبويبات الأخرى
    const handleAuthDataFromOtherTab = async (authData) => {
      // التحقق من صحة البيانات
      if (!authData || !authData.token) {
        // Invalid authentication data from other tab
        return;
      }
      
      // Authentication data received from another tab
      
      // تخزين بيانات المصادقة في التبويب الحالي
      sessionStorage.setItem('token', authData.token);
      sessionStorage.setItem('userType', authData.userType);
      sessionStorage.setItem('userRoleId', authData.userRoleId);
      sessionStorage.setItem('user', JSON.stringify(authData.user));
      // إزالة علامة الانتظار للمزامنة
      sessionStorage.removeItem('pendingAuthSync');
      
      try {
        // Updating application state in new tab
        
        // 1. أولاً: تحديث Redux بحالة المصادقة
        dispatch(setAuthState({
          isAuthenticated: true,
          token: authData.token,
          userType: authData.userType,
          userRoleId: authData.userRoleId,
          user: authData.user
        }));
        
        // 2. تحديث الصلاحيات بعد الحصول على بيانات المصادقة
        await syncUserPermissions();
        await dispatch(fetchPermissions());
        
        // 3. تحديث حالة الواجهة باستخدام الأحداث
        window.dispatchEvent(new Event('permissionsChanged'));
        window.dispatchEvent(new Event('authSyncCompleted'));
        // إطلاق حدث مخصص للتنقل بين الصفحات بعد المصادقة
        window.dispatchEvent(new CustomEvent('silentAuthUpdate', { detail: authData }));
        
        // Authentication data and permissions updated successfully from another tab
      } catch (error) {
        // Failed to update permissions after receiving authentication data
      }
    };
    
    // تهيئة قناة البث وتنفيذ عملية التهيئة
    const channelInitialized = setupBroadcastChannel();
    initializeTab();
    
    // التنظيف عند إزالة المكون
    return () => {
      // لا نغلق القناة بعد الآن، فقط نلغي تسجيل معالج الرسائل
      if (channelInitialized) {
        // استخدام unregisterMessageHandler للحفاظ على القناة مفتوحة بين الصفحات
        unregisterMessageHandler();
      }
    };
  }, [dispatch]); // الاعتماد على dispatch فقط
};

export default useTabInitialization;
